import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Avatar,
  Typography,
  Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  ApprovalComment,
  ApprovalCommentFormData,
} from "./approvalsCommentsTypes";
import { useApprovalComments } from "../pages/contractor/useApprovalComments";

interface ApprovalDiscussionProps {
  approvalId: string;
  userId: string;
  userRole: "contractor" | "client";
  userName?: string;
  showCommentForm?: boolean;
  onCommentAdded?: (comment: ApprovalComment) => void;
}

const ApprovalDiscussion: React.FC<ApprovalDiscussionProps> = ({
  approvalId,
  userId,
  userRole,
  userName = "You",
  showCommentForm = true,
  onCommentAdded,
}) => {
  const [comments, setComments] = useState<ApprovalComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingComment, setAddingComment] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { fetchComments, addComment } = useApprovalComments();

  const loadComments = useCallback(async () => {
    setLoading(true);
    const data = await fetchComments(approvalId);
    setComments(data);
    setLoading(false);
  }, [approvalId, fetchComments]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      setError("Comment cannot be empty");
      return;
    }

    setAddingComment(true);
    setError(null);

    const formData: ApprovalCommentFormData = {
      text: commentText.trim(),
    };

    const newComment = await addComment(approvalId, userId, formData, userRole);

    setAddingComment(false);

    if (newComment) {
      setComments((prev) => [...prev, newComment]);
      setCommentText("");
      onCommentAdded?.(newComment);
    } else {
      setError("Failed to add comment. Please try again.");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (name?: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getAvatarColor = (role: string) => {
    return role === "contractor" ? "#2563eb" : "#10b981";
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Comments Thread */}
      {comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No comments yet. {showCommentForm ? "Start the discussion!" : ""}
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            {comments.map((comment, index) => (
              <Box key={comment.id}>
                {index > 0 && <Divider />}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: getAvatarColor(comment.author_role),
                      fontSize: "0.75rem",
                    }}
                  >
                    {getInitials(comment.author_name)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {comment.author_name || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {comment.author_role === "contractor"
                          ? "Contractor"
                          : "Client"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(comment.created_at)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{comment.text}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Comment Form */}
      {showCommentForm && (
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 1 }}
            >
              {error}
            </Alert>
          )}
          <Stack spacing={1}>
            <TextField
              multiline
              rows={3}
              placeholder="Add your comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={addingComment}
              size="small"
              fullWidth
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleAddComment}
                disabled={addingComment || !commentText.trim()}
                size="small"
              >
                {addingComment ? "Sending..." : "Comment"}
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default ApprovalDiscussion;
